import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabel } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { CountryService } from '../../services/country/country-service';
import { UserAccessService } from '../../services/user-access-service';

const DRAFT_STORAGE_KEY = 'contact_form_draft';

interface ContactForm {
  name: FormControl<string | null>;
  email: FormControl<string | null>;
  phoneNumber: FormControl<string | null>;
  company: FormControl<string | null>;
  country: FormControl<string | null>;
  street: FormControl<string | null>;
  streetNumber: FormControl<string | null>;
  city: FormControl<string | null>;
  zip: FormControl<string | null>;
  message: FormControl<string | null>;
  dataConsent: FormControl<boolean | null>;
}

@Component({
  selector: 'app-contact',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputTextModule,
    ButtonModule,
    CheckboxModule,
    TextareaModule,
    FloatLabel,
    SelectModule,
    IftaLabelModule,
    TranslateModule,
  ],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
})
export class ContactComponent {
  contactForm: FormGroup<ContactForm>;
  private readonly userAccessService = inject(UserAccessService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly countryService = inject(CountryService);

  submitted = false;
  error = false;
  errorMessage = '';
  isSubmitting = false;

  readonly countries = this.countryService.countries;

  constructor() {
    this.contactForm = new FormGroup<ContactForm>({
      name: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          this.noOnlyWhitespaceValidator,
          this.noNumbersValidator,
        ],
      }),
      email: new FormControl<string | null>(null, {
        validators: [Validators.required, Validators.email, Validators.maxLength(255), this.emailPatternValidator],
      }),
      phoneNumber: new FormControl<string | null>(null, {
        validators: [Validators.required, this.e164PhoneValidator],
      }),
      company: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          this.noOnlyWhitespaceValidator,
        ],
      }),
      country: new FormControl<string | null>(null, { validators: [Validators.required] }),
      street: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          this.noOnlyWhitespaceValidator,
        ],
      }),
      streetNumber: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(10),
          this.noOnlyWhitespaceValidator,
        ],
      }),
      city: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          this.noOnlyWhitespaceValidator,
        ],
      }),
      zip: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(10),
          this.noOnlyWhitespaceValidator,
        ],
      }),
      message: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(1000),
          this.noOnlyWhitespaceValidator,
        ],
      }),
      dataConsent: new FormControl<boolean | null>(null, { validators: [Validators.requiredTrue] }),
    });

    this.restoreDraft();

    this.contactForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveDraft());

    this.applyCalculatorMessage();
  }

  /**
   * When the contact page is reached from the pricing calculator's "Get Started" button,
   * the calculator passes a pre-built summary in the router navigation state. Overwrite
   * only the message control, leaving any restored draft fields untouched. Emitting the
   * change persists the new message into the draft via the valueChanges subscription.
   */
  private applyCalculatorMessage(): void {
    const calculatorMessage = this.router.getCurrentNavigation()?.extras.state?.['calculatorMessage'];
    if (typeof calculatorMessage === 'string' && calculatorMessage.length > 0) {
      this.contactForm.controls.message.setValue(calculatorMessage);
    }
  }

  // Kompakte Validator-Factory
  private patternValidator(pattern: RegExp, errorKey: string, message: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      !control.value || pattern.test(control.value.toString().trim()) ? null : { [errorKey]: { message } };
  }

  private readonly e164PhoneValidator: ValidatorFn = this.patternValidator(
    /^\+[1-9]\d{1,14}$/,
    'e164Phone',
    'e164Phone',
  );

  private readonly emailPatternValidator: ValidatorFn = this.patternValidator(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    'emailPattern',
    'emailPattern',
  );

  private readonly noOnlyWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    !control.value || control.value.toString().trim().length > 0 ? null : { onlyWhitespace: true };

  private readonly noNumbersValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    !control.value || !/\d/.test(control.value.toString()) ? null : { noNumbers: true };

  getErrorMessage(fieldName: keyof ContactForm): string {
    const control = this.contactForm.get(fieldName);
    if (!control?.errors || !control.touched) return '';

    // Return only the first error found
    for (const [errorKey, errorValue] of Object.entries(control.errors)) {
      const translationKey = `contact.errors.${errorKey}`;

      if (errorKey === 'minlength' || errorKey === 'maxlength') {
        const lengthError = errorValue as { requiredLength: number };
        return this.translate.instant(translationKey, { requiredLength: lengthError.requiredLength });
      }

      return this.translate.instant(translationKey, {
        defaultValue: this.translate.instant('contact.errors.invalidInput'),
      });
    }
    return '';
  }

  hasError(fieldName: keyof ContactForm): boolean {
    const control = this.contactForm.get(fieldName);
    return !!control?.invalid && !!control?.touched;
  }

  async onSubmit() {
    this.contactForm.markAllAsTouched();
    if (!this.contactForm.valid) return;

    this.isSubmitting = true;
    this.error = false;
    this.errorMessage = '';

    try {
      await this.userAccessService.requestUserAccess(this.trimFormValues(this.contactForm.value));
      this.clearDraft();
      this.submitted = true;
      this.contactForm.reset();
    } catch {
      this.submitted = false;
      this.error = true;
      this.errorMessage = this.translate.instant('contact.errorGeneric');
    } finally {
      this.isSubmitting = false;
    }
  }

  private saveDraft(): void {
    const { dataConsent: _omit, ...draftFields } = this.contactForm.value;
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftFields));
    } catch {
      // Storage unavailable — fail silently
    }
  }

  private restoreDraft(): void {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const knownKeys = Object.keys(this.contactForm.controls).filter((k) => k !== 'dataConsent');
      const safe = Object.fromEntries(Object.entries(parsed).filter(([k]) => knownKeys.includes(k)));
      this.contactForm.patchValue(safe, { emitEvent: false });
    } catch {
      // Malformed or unavailable storage — fail silently
    }
  }

  private clearDraft(): void {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      // Storage unavailable — fail silently
    }
  }

  private trimFormValues(formValue: typeof this.contactForm.value): typeof this.contactForm.value {
    return Object.fromEntries(
      Object.entries(formValue)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]),
    ) as typeof this.contactForm.value;
  }
}
